import { useState, useEffect, useRef } from "react";
import cn from "classnames";
import Button from "../../../audi-ui-components/Button";
import Select from "../../../audi-ui-components/Select";
import TextField from "../../../audi-ui-components/TextField";
import RadioFieldset from "../../../audi-ui-components/RadioFieldset";
import Modal from "../../../audi-ui-components/Modal";
import Spinner from "../../../audi-ui-components/Spinner";
import RangeSlider from "../../../components/RangeSlider";
import API from "../../../functions/api";
import _find from "lodash/find";
import { formatDollar, unFormatNumber, formatNumberComma } from "../../../functions/textFormatting";
import { gtmPush } from "../../../functions/gtm";
import _debounce from "lodash/debounce";

const AFSCalculatorComponent = ({
  onCalculateRepayment,
  engine,
  amount,
  frequency,
  className,
  theme
}) => {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [products, setProducts] = useState([]);

  const [responseParams, setResponseParams] = useState({});
  const [responseCopy, setResponseCopy] = useState({});
  const [offer, setOffer] = useState(null);
  
  const [product, setProduct] = useState(null);
  const [purpose, setPurpose] = useState("P");
  // const [prodDesc, setProdDesc] = useState("");
  const [deposit, setDeposit] = useState(0);
  const [term, setTerm] = useState(0);
  const [balloon, setBalloon] = useState(0);
  const [kmlimit, setKmlimit] = useState(0);

  const [depositHasFocus, setDepositHasFocus] = useState(false);
  const [showDisclaimers, setShowDisclaimers] = useState(false);
  const [hasClickedDisclaimers, setHasClickedDisclaimers] = useState(false);

  const auiTheme = theme === "dark" ? "light" : null;

  const debouncedGtmPush = useRef(_debounce((category, action, label, data) => { gtmPush(category, action, label, data); }, 800));

  const calculate = (values) => {
    if (engine?.modelCode && amount) {
      setLoading(true);
      setError(null);
      let _product = values?.product || product;
      let _purpose = values?.purpose || purpose;
      let _deposit = values?.deposit || deposit;
      let _term = values?.term || term;
      let payloadParams = [
        { "id": "DownPayment", "text": _deposit, "changed": true },
        { "id": "Term", "text": _term, "changed": true },
        { "id": "InstallmentPaymentFrequency", "text": frequency, "changed": true },
      ];
      if (_product === "AudiChoice") {
        payloadParams.push(
          { "id": "FinanceProduct", "text": _purpose === "B" ? "ChattelMortgage" : "ConsumerLoan", "changed": true},
          { "id": "KMLimit", "text": values?.kmlimit || kmlimit, "changed": true },
        );
      } else {
        payloadParams.push({ "id": "Balloon", "text": values?.balloon || balloon, "changed": true });
      }
      let payload = {
        "product": {
          "parameter": payloadParams,
          "id": _product,
        },
        "vehicle": {
            "key": engine.modelCode,
            "year": engine.year,
            "priceTotal": unFormatNumber(amount),
            "priceModel": unFormatNumber(amount)
        },
        "domain": "AUDI.CC.AUS",
        "name": "Defaults"
      };
      API.post(
        `/1/afs/calculator`,
        payload
      ).then((response) => {
        // console.log(response.data);
        let summary = {};
        let params = {};
        let copy = {
          loanType: _product,
          purpose: purpose
        };
        if (response.data.summary && response.data.parameters) {
          if (response.data.summary.detailGroups?.detailGroup?.detail) {
            let len = response.data.summary.detailGroups.detailGroup.detail.length;
            for (let i=0; i<len; i++) {
              let d = response.data.summary.detailGroups.detailGroup.detail[i];
              summary[d.id] = d;
            }
          }
          if (response.data.parameters.group?.parameter) {
            let len = response.data.parameters.group.parameter.length;
            for (let i=0; i<len; i++) {
              let p = response.data.parameters.group.parameter[i];
              params[p.id] = p;
            }
          }
          copy.description = response.data.parameters.description;
          copy.productDisclaimer = response.data.parameters.productDisclaimer;
          copy.product = response.data.summary.product;
        }
        setResponseParams(params);
        setResponseCopy(copy);
        let comparisonRateLabel = summary.ComparisonRate?.label || "";
        let comparisonRateSymbol = comparisonRateLabel.charAt(comparisonRateLabel.length - 1);
        if (summary.ComparisonRate?.label && purpose === "B") {
          summary.ComparisonRate.label = `Interest Rate${comparisonRateSymbol}`;
        }
        if (summary.ComparisonRate?.label && purpose === "P") {
          summary.ComparisonRate.label = `Comparison Interest Rate${comparisonRateSymbol}`;
        }
        if (response.data.offerDetails?.rate) {
          let ofr = response.data.offerDetails;
          copy.offer = ofr;
          let d = new Date(ofr.endDate);
          ofr.endDateFormatted = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
          setOffer(ofr);
          if (summary.AnnualRate?.label) { summary.AnnualRate.label = "Finance Offer Rate##"; }
          if (summary.ComparisonRate?.label) { summary.ComparisonRate.label = "Finance Offer Rate##"; }
        } else {
          setOffer(null);
        }
        setLoading(false);
        onCalculateRepayment(summary, copy);
      }).catch((error) => {
        console.error(error);
        setLoading(false);
        setError("Could not calculate finance");
        onCalculateRepayment(null, null);
      });
    }
  }

  useEffect(() => {
    // get loan products
    API.get(
      `/1/afs/products`
    ).then((response) => {
      // console.log(response);
      if (response.data?.Products) {
        setProducts(response.data.Products.Product);
        setProduct(response.data.Products.Default);
        // let p = _find(response.data.Products.Product, {ID: response.data.Products.Default});
        // setPurpose(p.Purpose || "P");
        // setProdDesc(p.Description);
      }
      setLoading(false);
    }).catch((error) => {
      // console.error(error);
      setError("Could not load product data.");
      setLoading(false);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // get defaults
    if (engine?.modelCode && product) {
      setLoading(true);
      setError(null);
      let payload = {
        "product": {
          "id": product,
          "parameter":[{"id": "FinanceProduct", "text": purpose === "B" ? "ChattelMortgage" : "ConsumerLoan", "changed": true}]
        },
        "vehicle": {
            "key": engine.modelCode,
            "year": engine.year,
            "priceTotal": unFormatNumber(amount),
            "priceModel": unFormatNumber(amount)
        },
        "domain": "AUDI.CC.AUS",
        "name": "Defaults"
      };
      // if (product === "AudiChoice") {
      //   payload.product.parameter = [{ "id": "FinanceProduct", "text": purpose === "B" ? "ChattelMortgage" : "ConsumerLoan", "changed": true}];
      // }
      API.post(
        `/1/afs/default`,
        payload
      ).then((response) => {
        // console.log(response.data?.parameters?.group?.parameter);
        let params = response.data?.parameters?.group?.parameter || [];
        let newParams = {};
        let len = params.length;
        for (let i=0; i<len; i++) {
          let p = params[i];
          newParams[p.id] = p;
        }
        // console.log(newParams);
        setResponseParams(newParams);
        if (newParams.Term?.default) { setTerm(parseInt(newParams.Term.default)); }
        if (newParams.DownPayment?.dataValues) { setDeposit(parseFloat(newParams.DownPayment.dataValues)); }
        if (newParams.KMLimit?.default) { setKmlimit(parseInt(newParams.KMLimit.default)); }
        if (newParams.Balloon?.default) { setBalloon(parseInt(newParams.Balloon.default)); }
        // if (newParams.FinanceProduct?.default) { setPurpose(newParams.FinanceProduct.default); }
        let summary = {};
        if (response.data.summary?.detailGroups?.detailGroup?.detail) {
            let len = response.data.summary.detailGroups.detailGroup.detail.length;
            for (let i=0; i<len; i++) {
              let d = response.data.summary.detailGroups.detailGroup.detail[i];
              summary[d.id] = d;
            }
          }
        let copy = {
          loanType: product,
          purpose: purpose,
          description: response.data?.summary?.disclaimer,
          productDisclaimer: response.data?.summary?.product?.disclaimer
        };
        setResponseCopy(copy);
        let comparisonRateLabel = summary.ComparisonRate?.label || "";
        let comparisonRateSymbol = comparisonRateLabel.charAt(comparisonRateLabel.length - 1);
        if (summary.ComparisonRate?.label && purpose === "B") {
          summary.ComparisonRate.label = `Interest Rate${comparisonRateSymbol}`;
        }
        if (summary.ComparisonRate?.label && purpose === "P") {
          summary.ComparisonRate.label = `Comparison Interest Rate${comparisonRateSymbol}`;
        }
        if (response.data.offerDetails?.rate) {
          let ofr = response.data.offerDetails;
          copy.offer = ofr;
          let d = new Date(ofr.endDate);
          ofr.endDateFormatted = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
          setOffer(ofr);
          if (summary.AnnualRate?.label) { summary.AnnualRate.label = "Finance Offer Rate##"; }
          if (summary.ComparisonRate?.label) { summary.ComparisonRate.label = "Finance Offer Rate##"; }
        } else {
          setOffer(null);
        }
        copy.loanType = product;
        onCalculateRepayment(summary, copy);
        setLoading(false);
      }).catch((error) => {
        // console.error(error);
        setError("Could not get defaults");
        onCalculateRepayment(null, null);
        setLoading(false);
      });
    }
    // eslint-disable-next-line
  }, [engine?.modelCode, amount, product, purpose]);

  useEffect(() => {
    calculate();
  }, [frequency]);

  const renderTerm = () => {
    if (responseParams?.Term?.dataValues) {
      let len = responseParams.Term.dataValues.length;
      let termMin = parseInt(responseParams.Term.dataValues[0]);
      let termMax = parseInt(responseParams.Term.dataValues[len - 1]);
      let termStep = parseInt(responseParams.Term.dataValues[1]) - termMin;
      return (<>
        <div className="aui-textfield is-dirty aui-textfield--floating-label mb-2"><label className="aui-textfield__label">Term</label></div>
        <RangeSlider
          min={termMin} max={termMax} step={termStep}
          value={term}
          labels={{[termMin]: `${termMin}mths`, [termMax]: `${termMax}mths`}}
          handleLabel={term}
          onChange={(v) => { setTerm(v); }}
          onChangeComplete={()=>{ setTimeout(calculate, 500); gtmPush("fpcTermUpdated", "FinancePriceCalculator", term); }}
          className="mb-4"
          theme={theme}
        />
      </>);
    }
    return null;
  }

  const renderBalloon = () => {
    if (responseParams?.Balloon?.dataValues) {
      let len = responseParams.Balloon.dataValues.length;
      let bMin = parseInt(responseParams.Balloon.dataValues[0]);
      let bMax = parseInt(responseParams.Balloon.dataValues[len - 1]);
      let step = parseInt(responseParams.Balloon.dataValues[1]) - bMin;
      return (<>
        <div className="aui-textfield is-dirty aui-textfield--floating-label mb-2"><label className="aui-textfield__label">Balloon<sup>≠</sup></label></div>
        <RangeSlider
          min={bMin} max={bMax} step={step}
          value={balloon}
          labels={{[bMin]: `${bMin}%`, [bMax]: `${bMax}%`}}
          handleLabel={`${balloon?.toFixed(0)}%`}
          onChange={(v) => { setBalloon(v); }}
          onChangeComplete={()=>{ setTimeout(calculate, 500); gtmPush("fpcBalloonUpdated", "FinancePriceCalculator", `${balloon?.toFixed(0)}%`); }}
          className="mb-4"
          theme={theme}
        />
      </>);
    }
    return null;
  }

  const renderKMLimit = () => {
    if (responseParams?.KMLimit?.dataValues) {
      let opts = [];
      let len = responseParams.KMLimit.dataValues.length;
      for (let i=0; i<len; i++) {
        let t = responseParams.KMLimit.dataValues[i];
        opts.push({label: `${t}`, value: t});
      }
      return (
        <Select
          value={kmlimit}
          label={responseParams.KMLimit.label}
          description={responseParams.KMLimit.help}
          options={opts}
          onChange={(v) => { setKmlimit(v); calculate({kmlimit: v}); gtmPush("fpcAnnualKmLimitUpdated", "FinancePriceCalculator", v); }}
          className="mb-5"
          theme={auiTheme}
        />
      );
    }
    return null;
  }

  const getProdDesc = () => {
    let prod = _find(products, {ID: product});
    return prod?.Description || "";
  }
  
  return (
    <div className={cn("finance-calc-component", className)}>

      {(loading) && <div className="spinner-wrapper"><Spinner isContinuous /></div>}
      
      <h2 className="aui-headline-3 mb-4">Finance calculator</h2>
      
      {offer?.rate && <div className="my-3 aui-color-text-red">
        <p className="aui-small">Offer available based on your selections</p>
        <p><b>{offer.rate}% p.a. offer rate. Valid until {offer.endDateFormatted}</b></p>
      </div>}

      <p className="mb-2">Select your loan type</p>
      <div className="mb-4 product">
        <Button
          variant={purpose === "P" ? "primary" : "secondary"}
          onClick={()=>{ setPurpose("P"); setProduct("AudiChoice"); gtmPush("fpcLoanTypeUpdated", "FinancePriceCalculator", "AudiChoice"); }}
          theme={auiTheme}
        >Personal</Button>
        <Button
          variant={purpose === "B" ? "primary" : "secondary"}
          onClick={()=>{ setPurpose("B"); setProduct("AudiChoice"); gtmPush("fpcLoanTypeUpdated", "FinancePriceCalculator", "AudiChoice"); }}
          theme={auiTheme}
        >Business</Button>
      </div>
      <div className="mb-4">
        <RadioFieldset name="product" value={product} options={products.map(p => {
          if (p.ID === "NovatedLeaseR") { return null; }
          if (p.ID === "AudiChoice" || p.Purpose === purpose) {
            return {label: p.Label, value: p.ID}
          }
          return null;
        }).filter(el => el !== null)} onChange={(v) => {
          setProduct(v);
          gtmPush("fpcLoanTypeUpdated", "FinancePriceCalculator", v);
        }} theme={auiTheme} />
      </div>
      <div className="mb-4">{getProdDesc()}</div>

      {renderTerm()}
      
      {product && engine?.modelCode && responseParams?.DownPayment && <TextField
        name="deposit"
        label="Deposit"
        className="mb-4"
        value={depositHasFocus ? deposit : formatDollar(deposit)}
        onFocus={() => { setDepositHasFocus(true); }}
        onBlur={() => { setDepositHasFocus(false); calculate(); }}
        onChange={(v) => {
          setDeposit(unFormatNumber(v));
          debouncedGtmPush?.current("fpcDepositUpdated", "FinancePriceCalculator", formatDollar(v));
        }}
        theme={auiTheme}
      />}

      {product && engine?.modelCode && responseParams?.NAF && <div className="mb-4 aui-textfield is-dirty aui-textfield--floating-label">
        <p className="aui-textfield__label">{responseParams.NAF.label}</p>
        <p className="aui-textfield__input">{responseParams.NAF.units?.symbol}{formatNumberComma(responseParams.NAF.dataValues)}</p>
      </div>}
      
      {renderBalloon()}

      {renderKMLimit()}

      {error && <p className="py-3 aui-color-text-red"><b>{error}</b></p>}
      
      {(responseCopy?.description || responseCopy?.productDisclaimer) && <>
      <Button variant="text" onClick={()=>{
        setShowDisclaimers(true);
        if (!hasClickedDisclaimers) {
          gtmPush("fpcDisclaimersClicked", "FinancePriceCalculator", "finance");
          setHasClickedDisclaimers(true);
        }
      }} theme={auiTheme}>Finance disclaimers</Button>
      <Modal isActive={showDisclaimers} variant="layer" closeButton close={()=>{ setShowDisclaimers(false); }}>
        <div className="pe-5">
          {responseCopy.description && <div className="aui-color-text-gray90 mb-3" dangerouslySetInnerHTML={{__html: responseCopy.description}} />}
          {responseCopy.productDisclaimer && <div className="aui-color-text-gray90 mb-3" dangerouslySetInnerHTML={{__html: responseCopy.productDisclaimer}} />}
          {(product === "ConsumerLoan" || product === "ChattelMortgage") && 
            <p className="mb-3"><sup>≠</sup> Additional interest charges accrue if a lump sum agreed at the time of the loan ("balloon payment") is owed to AFS at the end of the loan term after all monthly repayments have been made.</p>
          }
          <p className="mb-3"><sup>##</sup> For approved applicants of Audi Financial Services (AFS) ABN 20 097 071 460, Australian Credit Licence 389344. Standard credit assessment and lending criteria apply. Excludes other offers. While stocks lasts. Fees, charges, T&Cs apply. A lump sum payment at the end of the loan may also apply. Dealer may charge to arrange finance, please ask them for details. The stated finance offer rate is historically applicable for a majority of AFS customers. The actual finance rate offered to a particular customer may vary depending on your credit profile, our credit criteria, the vehicle, amount financed, loan term and amount of deposit. AFS reserves the right to extend, withdraw or change all offers at any time.</p>
        </div>
      </Modal>
      </>}
    </div>
  );
}

export default AFSCalculatorComponent;
